// What a poster is told when POST /api/post does not go through.
//
// The create form used to render `e.message`, and apiPost sets that to
// `${path} ${status}`, so a poster who had used their three posts for the day
// was shown the literal string "/api/post 429" and a poster whose session had
// expired was shown "/api/post 401". Neither says what happened or what to do
// about it, and the rate limit is the one that matters: Program.cs allows
// 3 posts per user per 24 hours, so anyone working through a batch of listings
// meets it.
//
// There is deliberately no plan-limit branch. Plan.PostLimit is display data
// that no API code path reads, so no post has ever been refused for it.

/** The error apiPost throws: an Error carrying the HTTP status, or a tagged network failure. */
export interface CreatePostFailure {
  status?: number;
  code?: string;
  message?: string;
}

export function createPostError(e: CreatePostFailure | null | undefined): string {
  if (e?.code === 'network_failed') {
    return 'We could not reach JuicyStarz. Check your connection and try again. Nothing was posted.';
  }

  const status = Number(e?.status);

  // The "PostCreate" policy in Program.cs is a fixed window of 3 per user per
  // 24 hours. It rejects with a bare 429 and a Retry-After header the API
  // client does not surface, so do not promise a time we cannot compute.
  if (status === 429) {
    return 'You can create 3 posts in a 24 hour period and you have used all 3. Try again later, or edit one of the posts you already have.';
  }

  if (status === 401) {
    return 'Your session ended before this could be posted. Sign in again, then post it.';
  }

  // 422 is the content filter. Its only message today is one fixed generic
  // sentence, so tell the poster what to do rather than repeating it.
  if (status === 422) {
    return 'Some of the wording here is not allowed on JuicyStarz. Edit the title or the details and try again.';
  }

  if (status === 400) {
    return 'Something in this post was not accepted. Check the title, details, age and ZIP code, then try again.';
  }

  if (status >= 500) {
    return 'Something went wrong on our side and nothing was posted. Try again in a minute.';
  }

  return 'We could not create this post. Nothing was saved, so you can try again.';
}
